// Shared utilities for event scheduling (used by event-detail and match-scheduler)

export const SCHOOL_COLORS = [
  { bg: "bg-emerald-100", border: "border-emerald-300", text: "text-emerald-800" },
  { bg: "bg-blue-100", border: "border-blue-300", text: "text-blue-800" },
  { bg: "bg-orange-100", border: "border-orange-300", text: "text-orange-800" },
  { bg: "bg-purple-100", border: "border-purple-300", text: "text-purple-800" },
  { bg: "bg-amber-100", border: "border-amber-300", text: "text-amber-800" },
  { bg: "bg-pink-100", border: "border-pink-300", text: "text-pink-800" },
  { bg: "bg-teal-100", border: "border-teal-300", text: "text-teal-800" },
  { bg: "bg-indigo-100", border: "border-indigo-300", text: "text-indigo-800" },
  { bg: "bg-rose-100", border: "border-rose-300", text: "text-rose-800" },
];

// Court/field colors -- deliberately avoids SCHOOL_COLORS palette
export const COURT_COLORS = [
  { bg: "bg-sky-100", border: "border-sky-300", text: "text-sky-800", headerBg: "bg-sky-200", columnBg: "bg-sky-50/40" },
  { bg: "bg-lime-100", border: "border-lime-300", text: "text-lime-800", headerBg: "bg-lime-200", columnBg: "bg-lime-50/40" },
  { bg: "bg-violet-100", border: "border-violet-300", text: "text-violet-800", headerBg: "bg-violet-200", columnBg: "bg-violet-50/40" },
  { bg: "bg-cyan-100", border: "border-cyan-300", text: "text-cyan-800", headerBg: "bg-cyan-200", columnBg: "bg-cyan-50/40" },
  { bg: "bg-fuchsia-100", border: "border-fuchsia-300", text: "text-fuchsia-800", headerBg: "bg-fuchsia-200", columnBg: "bg-fuchsia-50/40" },
  { bg: "bg-stone-100", border: "border-stone-300", text: "text-stone-800", headerBg: "bg-stone-200", columnBg: "bg-stone-50/40" },
  { bg: "bg-yellow-100", border: "border-yellow-300", text: "text-yellow-800", headerBg: "bg-yellow-200", columnBg: "bg-yellow-50/40" },
  { bg: "bg-slate-100", border: "border-slate-300", text: "text-slate-800", headerBg: "bg-slate-200", columnBg: "bg-slate-50/40" },
];

export type DraggableTeam = {
  id: string;
  name: string;
  schoolName: string;
  teamId: string | null; // null for external/manual teams
  ageGroupId: string | null;
  ageGroupName: string | null;
  defaultFirstHalfMins: number | null;
  defaultBreakMins: number | null;
  defaultSecondHalfMins: number | null;
  colorIndex: number;
};

export type GridSlot =
  | { type: "pending"; team: DraggableTeam }
  | { type: "match"; matchId: string; name: string; home: DraggableTeam; away: DraggableTeam | null };

export function slotKey(courtId: string, time: string): string {
  return `${courtId}:${time}`;
}

export function parseSlotKey(key: string): { courtId: string; time: string } {
  const i = key.indexOf(":");
  return { courtId: key.slice(0, i), time: key.slice(i + 1) };
}

/** Generate HH:MM slot labels between two ISO timestamps at the given step (minutes).
 *  The start is floored to a clean boundary of `stepMins`. */
export function generateTimeSlots(startsAt: string, endsAt: string, stepMins = 30): string[] {
  const start = new Date(startsAt);
  const end = new Date(endsAt);
  start.setMinutes(Math.floor(start.getMinutes() / stepMins) * stepMins, 0, 0);
  const slots: string[] = [];
  const cur = new Date(start);
  while (cur < end) {
    const h = cur.getHours().toString().padStart(2, "0");
    const m = cur.getMinutes().toString().padStart(2, "0");
    slots.push(`${h}:${m}`);
    cur.setMinutes(cur.getMinutes() + stepMins);
  }
  return slots;
}

/** Get the HH:MM slot key for a match (rounded down to nearest `stepMins` min) */
export function matchSlotKey(iso: string, stepMins = 30): string {
  const d = new Date(iso);
  const h = d.getHours().toString().padStart(2, "0");
  const m = (Math.floor(d.getMinutes() / stepMins) * stepMins).toString().padStart(2, "0");
  return `${h}:${m}`;
}

/** Format ISO to HH:MM */
export function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/** Format ISO to readable date */
export function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString([], {
    weekday: "short", day: "numeric", month: "short", year: "numeric",
  });
}
